import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Form, Button, Row, Col } from "react-bootstrap";
import FormContainer from "../components/FormContainer";
import Loading from "../components/Loading";
import Message from "../components/Message";
import Meta from "../components/Meta";
import { register, verifyEmailOTP, resendOtp } from "../redux/actions/userActions";
import {
  USER_REGISTER_RESET,
  USER_VERIFY_OTP_RESET,
} from "../redux/constants/userConstants";

const RegisterScreen = () => {
  let navigate = useNavigate();
  const dispatch = useDispatch();

  // Registration fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [err, setErr] = useState("");

  // OTP field
  const [otp, setOtp] = useState("");
  const [waitingForOtp, setWaitingForOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const userRegister = useSelector((state) => state.userRegister);
  const { userInfo: registeredUser, loading, error } = userRegister;

  const userVerifyOtp = useSelector((state) => state.userVerifyOtp);
  const { loading: loadingOtp, error: errorOtp, success: successOtp } = userVerifyOtp;

  // Reset register and OTP states on component mount
  useEffect(() => {
    dispatch({ type: USER_REGISTER_RESET });
    dispatch({ type: USER_VERIFY_OTP_RESET });

    if (successOtp) {
      // OTP verified, redirect to home
      navigate("/");
    }
  }, [dispatch, navigate, successOtp]);

  // Set waitingForOtp only when registration is successful
  useEffect(() => {
    if (registeredUser) {
      setWaitingForOtp(true);
    }
  }, [registeredUser]);

  // Resend OTP cooldown timer logic
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    setErr("");

    if (password !== confirmPassword) {
      setErr("Passwords do not match");
      return;
    }

    if (
      name.trim() === "" ||
      email.trim() === "" ||
      password.trim() === "" ||
      contactNumber.trim() === ""
    ) {
      setErr("Please fill all required fields");
      return;
    }

    // E.164 validation
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    if (!e164Regex.test(contactNumber)) {
      setErr("Enter valid number in E.164 format. Example: +919876543210");
      return;
    }

    dispatch(register(name, email, password, contactNumber));
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (otp.trim() === "") {
      setErr("Please enter the OTP");
    } else {
      dispatch(verifyEmailOTP(email, otp));
    }
  };

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    dispatch(resendOtp(email));
    setResendCooldown(120);
  };

  return (
    <>
      <Meta title="SYUKRA | Register" />
      <FormContainer>
        {!waitingForOtp ? (
          <>
            <h2>Sign Up</h2>
            <Form onSubmit={handleRegisterSubmit}>
              {error && <Message variant="danger">{error}</Message>}
              {err && <Message variant="danger">{err}</Message>}
              
              <Form.Group controlId="name" className="mt-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>
              
              <Form.Group controlId="contactNumber" className="mt-3">
                <Form.Label>Contact Number (e.g., +91xxxxxxxxxx)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="+919876543210"
                  value={contactNumber}
                  onChange={(e) => {
                    let val = e.target.value;

                    val = val.replace(/\s+/g, ""); // remove spaces

                    if (!val.startsWith("+")) {
                      val = "+" + val.replace(/[^0-9]/g, "");
                    } else {
                      val = "+" + val.substring(1).replace(/[^0-9]/g, "");
                    }

                    setContactNumber(val);
                  }}
                />
              </Form.Group>
              
              <Form.Group controlId="email" className="mt-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>
              
              <Form.Group controlId="password" className="mt-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Group>
              
              <Form.Group controlId="confirmPassword" className="mt-3">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Form.Group>
              
              <Button className="mt-3" type="submit" variant="primary" disabled={loading}>
                {loading ? <Loading /> : "Sign Up"}
              </Button>
            </Form>
            <Row className="py-3">
              <Col>
                Have an account? <Link to="/login">Login</Link>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <h2>Verify Email</h2>
            <p>
              An OTP has been sent to <strong>{email}</strong>. Please enter it below:
            </p>
            <Form onSubmit={handleOtpSubmit}>
              {errorOtp && <Message variant="danger">{errorOtp}</Message>}
              {err && <Message variant="danger">{err}</Message>}
              <Form.Group controlId="otp" className="mt-3">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </Form.Group>
              <Button className="mt-3" type="submit" variant="primary" disabled={loadingOtp}>
                {loadingOtp ? <Loading /> : "Verify"}
              </Button>
            </Form>
            <Button
              variant="link"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0}
            >
              Resend OTP {resendCooldown > 0 ? `(${resendCooldown}s)` : ""}
            </Button>
            <Row className="py-3">
              <Col>
                Back to Register <Button variant="link" onClick={() => setWaitingForOtp(false)}>Go Back</Button>
              </Col>
            </Row>
          </>
        )}
      </FormContainer>
    </>
  );
};

export default RegisterScreen;
